import { useEffect, useState } from "react";
import { ApiRequestError } from "./api.ts";

/** Debounce a fast-changing value (e.g. a search box) by `ms`. */
export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Format an ISO/epoch date string as YYYY-MM-DD (or "—"). */
export function fmtDate(s: string | null): string {
  if (!s) return "—";
  const ms = Number.isNaN(Number(s)) ? s : Number(s);
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? String(s) : d.toISOString().slice(0, 10);
}

/** Extract a human message from an API error (or fall back). */
export function errMsg(e: unknown, fallback: string): string {
  if (e instanceof ApiRequestError) return e.body.error.message || fallback;
  return e instanceof Error ? e.message : fallback;
}

/** Format integer cents as a currency string. */
export function fmtMoney(cents: number, currency = "USD"): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(cents / 100);
}

/** Customer dashboard URL for an organization (opens that org's workspace). */
export function dashboardOrgUrl(orgId: string): string {
  const base =
    (import.meta.env["VITE_DASHBOARD_URL"] as string | undefined) ?? "https://app.currency-core.com";
  return `${base.replace(/\/$/, "")}/dashboard/${orgId}`;
}

/** Multi-select filter map → query params (comma-joined; empty keys dropped). */
export function filterParams(filters: Record<string, string[]>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v.length) out[k] = v.join(",");
  }
  return out;
}
